import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import EntitiesList from '@features/entities/entities-list/ui/EntitiesList/EntitiesList';
import EvEntityRowForm from '@features/ev-editor/EvEntityRowForm';
import { useGroupSelectionContext } from '@context/groupSelectionContext';
import { Form, Row } from '@components/form/Form/Form';
import * as TextField from '@components/form/TextField/TextField';
import { getFormDataObj, isErrorResponse, safelyParseJson } from '@utils/utils';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectSelectedFeatures } from '@redux/selectors/selectors';
import { SelectedFeatureProperties } from '@redux/showInfo/types';
import { useToastContext } from '@context/toastContext';

const GroupSelectionSetsList = () => {
    const [newSetName, setNewSetName] = React.useState('');
    const [selectedId, setSelectedId] = React.useState('');
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const {
        loading,
        availableSelectionSets,
        getGroupSelectionSets,
        getGroupSelectionSetById,
        createGroupSelectionSet,
        deleteGroupSelectionSet,
    } = useGroupSelectionContext();
    const { addToast } = useToastContext();
    const { t } = useTranslation();

    useEffect(() => {
        const eventHandler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSelectedId('');
            }
        };

        document.addEventListener('keyup', eventHandler);

        return () => {
            document.removeEventListener('keyup', eventHandler);
        };
    }, [setSelectedId]);

    const onRowClick = useCallback(
        async (entity: any) => {
            const { id } = entity;
            setSelectedId(id);

            const resp = await getGroupSelectionSetById(id);
            if (isErrorResponse(resp)) {
                addToast(resp.error_description, 'error');
                return;
            }
            const { name } = resp;
            setNewSetName(name);
        },
        [getGroupSelectionSetById, setNewSetName]
    );

    const deleteHandler = useCallback(
        async (id: string) => {
            const resp = await deleteGroupSelectionSet(id);
            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                addToast(t('common.save-successful'), 'success');
            }
        },
        [deleteGroupSelectionSet]
    );

    const onFormSubmit = useCallback(
        async (
            event: React.FormEvent<HTMLFormElement>,
            selectedFeatures: SelectedFeatureProperties[]
        ) => {
            const formDataEntryObj = getFormDataObj(event);
            const resp = await createGroupSelectionSet(
                formDataEntryObj['name'] as string,
                selectedFeatures.map(({ id }) => id)
            );
            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                setNewSetName(resp.name);
                addToast(t('common.save-successful'), 'success');
            }
        },
        [createGroupSelectionSet, getGroupSelectionSets]
    );

    const textfieldHandler = debounce(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setNewSetName(value);
    }, 500);

    return (
        <div className="group-selection-sets">
            <p>Saved selections</p>
            <div>
                <Form
                    name="group-selection-sets-list-form"
                    onSubmit={event => onFormSubmit(event, selectedFeatures)}
                    submitBtnTitle={t('road-network.group-selection-save-set-submit-btn')}
                >
                    <Row>
                        <TextField.default
                            name="name"
                            type="text"
                            value={newSetName}
                            onChange={textfieldHandler}
                        />
                    </Row>
                </Form>
            </div>
            <div className="group-selection-sets-list">
                {availableSelectionSets.length ? (
                    <EntitiesList
                        loading={loading}
                        entities={availableSelectionSets}
                        onRowClick={onRowClick}
                        form={EvEntityRowForm}
                        deleteFn={deleteHandler}
                        loadDataFn={getGroupSelectionSets}
                        selectedId={selectedId}
                        // menuActions={menuActions}
                    />
                ) : (
                    <div className="group-selection-no-sets">
                        {t('road-network.group-selection-no-sets')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupSelectionSetsList;
